/**
 * Uniqueness API
 *
 * Provides a unified interface for checking name uniqueness across all entity types.
 * This enables real-time validation as users type, preventing conflicts before submission.
 */

import { request } from './core'
import { buildQueryString } from '../../utils/apiQueryBuilder'

/**
 * Supported entity types for uniqueness checking.
 */
export type EntityType =
  | 'project'
  | 'skill'
  | 'agent'
  | 'connector'
  | 'tool'
  | 'prompt'
  | 'iam'
  | 'model'
  | 'knowledge'
  | 'blueprint'

/**
 * Parameters for uniqueness check request.
 */
export interface UniquenessCheckParams {
  /** Type of entity to check */
  entity_type: EntityType
  /** Name to check for uniqueness */
  name: string
  /** Entity ID to exclude (for update scenarios) */
  exclude_id?: string
  /** Project ID context (optional, for future use) */
  project_id?: string
  /** Connector connection ID (knowledge: scope display_name to this connection) */
  connector_id?: string
  /** Physical table/collection to exclude (knowledge: same collection re-register) */
  exclude_physical_table?: string
  /** AbortSignal for request cancellation */
  signal?: AbortSignal
  /** Platform admin: scope uniqueness check to a target tenant */
  tenantId?: string
}

/**
 * Response from uniqueness check.
 */
export interface UniquenessCheckResponse {
  /** Whether the name is unique across all tenants */
  is_unique: boolean
  /** Message explaining the result (present when not unique) */
  message?: string | null
}

/**
 * Uniqueness API client.
 *
 * @example
 * ```typescript
 * // Check if a project name is available
 * const result = await uniquenessApi.check({
 *   entity_type: 'project',
 *   name: 'My New Project',
 * });
 *
 * if (!result.is_unique) {
 *   console.log(result.message); // "A project with this name already exists"
 * }
 *
 * // Check for update (exclude current entity)
 * const updateResult = await uniquenessApi.check({
 *   entity_type: 'project',
 *   name: 'Updated Name',
 *   exclude_id: 'current-project-id',
 * });
 * ```
 */
export const uniquenessApi = {
  /**
   * Check if a name is unique globally across all tenants.
   *
   * @param params - Uniqueness check parameters
   * @returns Promise with uniqueness result
   */
  check: (params: UniquenessCheckParams): Promise<UniquenessCheckResponse> => {
    const { signal, tenantId, ...queryParams } = params
    const query = buildQueryString(queryParams)
    return request<UniquenessCheckResponse>(`/uniqueness/check${query}`, { signal, tenantId })
  },
}
